import moment from 'moment'
import http from "../../core/services/http.service";
import {AppSettings} from "@/shared/AppSettings";
import LotsService from "@/modules/lots/LotsService";
import bucketHelperService from "@/core/services/bucketHelper.service";
import {ON_PDF_MODAL_OPEN, ON_PDF_MODAL_CLOSE, ON_AUCTIONEERS_SHEET_LOCKED} from "@/plugins/event-bus/event-bus-names";

const SalesService = {
  getSales(params = null) {
    return http.get('/api/v1/auctions/', {params});
  },
  getSalesForLLP(params = null) {
    return http.get('/api/v1/auctions/llp/', {params});
  },
  getSalesForLLPLots(params = null) {
    return http.get('/api/v1/auctions/', {
      params: {
        status: 'published',
        type: 'timed',
        start_date_before: moment().utc().format('Y-M-D H:mm:ss'),
        expand: 'images,currency',
        ...params
      }
    });
  },
  getSale(id, expand, params = {}) {
    return http.get(`/api/v1/auctions/${id}/?expand=${expand}`, {params});
  },
  getSaleForLLP(id, params = null) {
    return http.get(`/api/v1/auctions/llp/${id}/`, {params});
  },
  saleExists(saleNo, expand = '') {
    return http.get(`/api/v1/auctions/exists/${saleNo}/?expand=${expand}`);
  },
  getSaleInfo(id, expand = '', params = {}) {
    return http.get(`/api/v1/auctions/${id}/info/?expand=${expand}`, {params});
  },
  updateSale(id, data, expand = '') {
    return http.put(`/api/v1/auctions/${id}/?expand=${expand}`, data);
  },
  createSale(data) {
    return http.post('/api/v1/auctions/', data);
  },
  partialUpdateSale(id, data, expand = '') {
    return http.patch(`/api/v1/auctions/${id}/?expand=${expand}`, data);
  },
  createOrUpdateSale(data) {
    if (data.uuid) {
      return this.updateSale(data.uuid, data);
    }
    return this.createSale(data);
  },
  removeSale(uuid) {
    return http.delete(`/api/v1/auctions/${uuid}`)
  },
  getSaleVendors(uuid) {
    return http.get(`/api/v1/auctions/${uuid}/vendors/`);
  },
  getPreBids(params = null) {
    return http.get('/api/v1/pre-bids/', {params});
  },
  createPreBids(data) {
    return http.post('/api/v1/pre-bids/', data);
  },
  batchRemovePreBids(uuid) {
    return http.delete(`/api/v1/pre-bids/`, {
      data: {uuid}
    });
  },
  syncToLive(saleNo, action, provider_name) {
    return http.post(`/api/v1/auctions/live/${saleNo}/sync`, {action, provider_name});
  },
  exportAuctionProviderData(saleNo, action, provider_name) {
    return http.post(`/api/v1/auctions/live/${saleNo}/export/`, {action, provider_name});
  },
  updateStatus(status, data) {
    return http.post(`/api/v1/auctions/${status}/`, data);
  },
  pauseResumeAction(uuid, payload) {
    return http.post(`/api/v1/auctions/${uuid}/pause-resume/`, payload);
  },
  uploadSaleResults(saleNo, data, onUploadProgress) {
    return http.post(`/api/v1/auctions/live/${saleNo}/results/`, data, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: onUploadProgress
    })
  },
  async getBatchLotUpData(auctionUuid) {
    return LotsService.getReservations({
      limit: 10000,
      auction_uuid: auctionUuid,
      ordering: 'lot_no',
      expand: 'inventory.receipt.customer,inventory.department,inventory.item_location,inventory.category,inventory.images,lot'
    });
  },
  batchLotUp(saleNo, data) {
    return http.post(`/api/v1/inventories/reservations/${saleNo}/batch-lot-up/`, data);
  },
  createCatalogs(data) {
    return http.post(AppSettings.getUrl('/api/v1/auctions/catalogs/'), data, {
      headers: {'Content-Type': 'multipart/form-data'}
    });
  },
  removeCatalog(saleUuid, uuids) {
    return http.delete(AppSettings.getUrl(`/api/v1/auctions/${saleUuid}/catalogs/`), {data: {uuids: uuids}});
  },
  getImages(uuid) {
    return http.get(`/api/v1/auctions/${uuid}/images/`);
  },
  removeImage(saleUuid, uuids) {
    return http.delete(`/api/v1/auctions/${saleUuid}/images/`, {data: {uuids: uuids}});
  },
  async getDashboardProfileInfo(params) {
    return http.get(`/api/v1/auctions/dashboard/profile-info/`, {params});
  },
  async getDashboardGeneralInfo(params) {
    return http.get(`/api/v1/auctions/dashboard/general-info/`, {params});
  },
  async getDashboardProjectionInfo(params) {
    return http.get(`/api/v1/auctions/dashboard/projection-info/`, {params});
  },
  getDashboardTotalVat(params = null) {
    return http.get('/api/v1/auctions/dashboard/total-vat/', {params});
  },
  async getDashboardItemsByCategory(params) {
    let response = await http.get(`/api/v1/auctions/dashboard/items-by-category/`, {params});

    const returnData = [];

    if (response.success) {
      response.body.forEach(item => {
        returnData.push({name: item.key, value: item.value})
      });
    }

    return returnData;
  },
  async createRenameAndDeleteImages(saleUuid, images, url, progressEvent) {
    let jsonData = {'auction_uuid': saleUuid}
    return bucketHelperService.createRenameAndDeleteImages(images, url, 'auction', jsonData, progressEvent)
  },
  async viewAuctionCatalog(vm, uuid, saleNo, regenerate = false, lotFilterPayload = {}) {
    let response = await http.post(`/api/v1/auctions/${uuid}/view-catalog/`, {regenerate, ...lotFilterPayload});
    if (response.success) {
      if (response.body.url) {
        vm.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: vm.$i18n.t(`Auction Catalog for {sale_no}`, {sale_no: saleNo}),
          data: {pdf_url: response.body.url}
        });
      } else {
        vm.$toast('success', vm.$i18n.t("Auction catalog generation has successfully started."))
      }
    } else {
      if (response.body.non_field_errors) {
        const listItemsArr = [];
        response.body.non_field_errors.forEach(err => listItemsArr.push(vm.$createElement('li', {}, err)))
        vm.$errorDialog(vm.$createElement('ul', {class: "mb-0"}, listItemsArr), vm.$i18n.t('Catalog Generation Error'));
      }
    }
  },
  sendProposalOfUnsoldLotsNotification({auction_uuid, customer_uuid, data}) {
    return http.post(`/api/v1/auctions/${auction_uuid}/proposal-of-unsold-lots/${customer_uuid}`, data);
  },
  getProposalOfUnsoldLots({auction_uuid, customer_uuid}) {
    return http.get(`/api/v1/auctions/${auction_uuid}/proposal-of-unsold-lots/${customer_uuid}`);
  },
  sendAuctionConfirmationNotification({auction_uuid, customer_uuid, confirmed}) {
    return http.post(`/api/v1/auctions/${auction_uuid}/send-auction-confirmation-notification/`, {
      customer_uuid,
      confirmed
    });
  },
  batchSendAuctionConfirmationNotification(data) {
    return http.post(`/api/v1/auctions/batch-send-auction-confirmation-notification/`, data);
  },
  updateCategoryLotRanges(auction_uuid, category_uuid, start_lot_number, end_lot_number) {
    return http.post(`/api/v1/auctions/${auction_uuid}/update-category-lot-ranges/`, {
      category_uuid,
      start_lot_number,
      end_lot_number
    });
  },
  exportSaleFile(uuid, params = {}) {
    return http.post(AppSettings.getUrl(`/api/v1/auctions/${uuid}/file-export/`), params);
  },
  getSaleCollections(params = null) {
    return http.get('/api/v1/auctions/auction-collections/', {params});
  },
  createSaleCollections(data) {
    return http.post('/api/v1/auctions/auction-collections/', data);
  },
  updateSaleCollections(id, data, expand = '') {
    return http.put(`/api/v1/auctions/auction-collections/${id}/?expand=${expand}`, data);
  },
  removeSaleCollection(uuid) {
    return http.delete(`/api/v1/auctions/auction-collections/${uuid}/`);
  },
  sendAuctionResultsNotification({auction_uuid, customer_uuid, confirmed}) {
    return http.post(`/api/v1/auctions/${auction_uuid}/send-auction-results-notification/`, {customer_uuid, confirmed});
  },
  batchSendAuctionResultsNotification(data) {
    return http.post(`/api/v1/auctions/batch-send-auction-results-notification/`, data);
  },
  async generateOrRetrieveAuctioneersSheet(vm, sale_uuid, generate=false, hideButtons=false) {
    let response = await http.post(`/api/v1/auctions/${sale_uuid}/auctioneers-sheet/`, {generate});
    if (response.success) {
      if (response.body.pdf_url) {
        let modalActions = [];
        if (!hideButtons) {
          if (response.body.show_lock_button) {
            modalActions.push({
              display_name: vm.$i18n.t("Lock Auctioneer's Sheet"),
              handler: () => SalesService.lockUnlockPV(vm, sale_uuid, 1, true)
            });
          }
          if (response.body.show_unlock_button) {
            modalActions.push({
              display_name: vm.$i18n.t("Unlock Auctioneer's Sheet"),
              handler: () => SalesService.lockUnlockPV(vm, sale_uuid, 1, false)
            });
          }
        }
        vm.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: vm.$i18n.t(`Auctioneer's Sheet for {sale_no}`, {sale_no: response.body.sale_no}),
          data: response.body,
          actions: modalActions
        });
      } else {
        vm.$toast('success', vm.$i18n.t("Auctioneer's sheet generation has successfully started."))
      }
    } else {
      if (response.body.non_field_errors) {
        const listItemsArr = [];
        response.body.non_field_errors.forEach(err => listItemsArr.push(vm.$createElement('li', {}, err)))
        vm.$errorDialog(vm.$createElement('ul', {class: "mb-0"}, listItemsArr), vm.$i18n.t("Auctioneer's sheet Generation Error"));
      } else {
        vm.$errorDialog(response.body.detail);
      }
    }
  },
  async generateOrRetrieveAfterSaleAuctioneersSheet(vm, sale_uuid, generate=false, hideButtons=false) {
    let response = await http.post(`/api/v1/auctions/${sale_uuid}/after-sale-auctioneers-sheet/`, {generate});
    if (response.success) {
      if (response.body.pdf_url) {
        let modalActions = [];
        if (!hideButtons) {
          if (response.body.show_lock_button) {
            modalActions.push({
              display_name: vm.$i18n.t("Lock After Sale Auctioneer's Sheet"),
              handler: () => SalesService.lockUnlockPV(vm, sale_uuid, 2, true)
            });
          }
          if (response.body.show_unlock_button) {
            modalActions.push({
              display_name: vm.$i18n.t("Unlock After Sale Auctioneer's Sheet"),
              handler: () => SalesService.lockUnlockPV(vm, sale_uuid, 2, false)
            });
          }
        }
        vm.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: vm.$i18n.t(`After Sale Auctioneer's Sheet for {sale_no}`, {sale_no: response.body.sale_no}),
          data: response.body,
          actions: modalActions
        });
      } else {
        vm.$toast('success', vm.$i18n.t("After Sale Auctioneer's sheet generation has successfully started."))
      }
    } else {
      if (response.body.non_field_errors) {
        const listItemsArr = [];
        response.body.non_field_errors.forEach(err => listItemsArr.push(vm.$createElement('li', {}, err)))
        vm.$errorDialog(vm.$createElement('ul', {class: "mb-0"}, listItemsArr), vm.$i18n.t("After Sale Auctioneer's sheet Generation Error"));
      } else {
        vm.$errorDialog(response.body.detail);
      }
    }
  },
  async lockUnlockPV(vm, uuid, type, lock) {
    let success = await vm.$confirm(
        null,
        null,
        null,
        null,
        vm.$i18n.t('Please Confirm'),
        vm.$i18n.t('Yes'),
        vm.$i18n.t('No'),
        null,
        {
          label: vm.$i18n.t(`Please type 'confirm' to {lock_string}`, {lock_string: (lock ? 'lock' : 'unlock')}),
          text: 'confirm'
        }
    )
    if (!success) {
      return;
    }
    let response = await http.post(`/api/v1/auctions/${uuid}/lock-unlock-auctioneers-sheet/`, {type, lock})
    if (response.success) {
      let title = type === 1 ? vm.$i18n.t("Auctioneer's Sheet") : vm.$i18n.t("After Sale Auctioneer's Sheet");
      let statusText = lock ? 'Locked' : 'Draft';
      vm.$evBus.$emit(ON_AUCTIONEERS_SHEET_LOCKED, {
        reportType: type,
        statusText: lock ? 'Locked' : 'Draft'
      });
      vm.$evBus.$emit(ON_PDF_MODAL_CLOSE);
      vm.$evBus.$emit(ON_PDF_MODAL_OPEN, {
        title: title,
        data: response.body,
        actions: [{
          display_name: vm.$i18n.t(`{lock_string} {title}`, {lock_string: (lock ? 'Unlock' : 'Lock'), title: title}),
          handler: () => SalesService.lockUnlockPV(vm, uuid, type, !lock)
        }]
      });
    } else {
      await vm.$errorDialog(vm.$i18n.t('Something went wrong'));
    }
  },
  getAuctionCategoryTerms(params = null) {
    return http.get('/api/v1/auctions/auction-category-terms/', {params});
  },
  createAuctionCategoryTerm(data, expand='') {
    return http.post(`/api/v1/auctions/auction-category-terms/?expand=${expand}`, data);
  },
  updateAuctionCategoryTerm(id, data, expand = '') {
    return http.put(`/api/v1/auctions/auction-category-terms/${id}/?expand=${expand}`, data);
  },
  removeAuctionCategoryTerm(uuid) {
    return http.delete(`/api/v1/auctions/auction-category-terms/${uuid}/`)
  },
  getSaleCategories(uuid) {
    return http.get(`/api/v1/auctions/${uuid}/categories/`)
  },
  batchProposalOfUnsoldLotsNotification(data) {
    return http.post(`/api/v1/auctions/batch-send-proposal-of-unsold-lots-notification/`, data);
  },
  async getCollectionAppointments(uuid) {
    let response = await http.get(`/api/v1/auctions/${uuid}/collection-appointments/`);
    return response;
  },
  async saveCollectionAppointments(uuid, data) {
    let response = await http.post(`/api/v1/auctions/${uuid}/collection-appointments/`, data);
    return response;
  }
};

export default SalesService;
